import { useEffect, useRef, type ReactNode } from 'react';
import { Portal } from './portal';
import './modal.css';

/**
 * The demo's modal grammar: a scrim and one ivory panel. 560px for the add box,
 * 420px for the correct-this popover and the wrong-person confirm, 640px for the
 * two foot tables (run history and the decision log), whose rows are wide and
 * whose body scrolls inside `.modal-body`.
 *
 * **Keyboard behaviour lives here rather than in each caller** — the same
 * argument the record's status control makes one level down, applied to the
 * component every modal in the product already goes through.
 *
 * A dialog that says `aria-modal="true"` is promising that nothing behind it is
 * reachable, and until 2026-09-06 that promise was false in three ways at once:
 * focus stayed on whatever the reader had clicked, Tab walked straight out of
 * the panel into the page underneath, and closing left focus on an element that
 * was often no longer in the document — which drops it to `<body>` and starts
 * the next Tab from the top of the app. Every modal had all three.
 *
 * Three things, in the order they happen:
 *
 *  1. **Initial focus.** The first focusable thing inside the panel, or the
 *     panel itself when there is none. It carries `tabIndex={-1}` for exactly
 *     that case: a modal that is a confirmation has buttons, and one that is a
 *     report has nothing but text.
 *  2. **The trap.** Tab and Shift+Tab wrap within the panel. The stops are
 *     computed on each press rather than once, because a modal's controls come
 *     and go — the working button swaps for a spinner, an error line appears.
 *  3. **Return.** Whatever had focus when the modal opened gets it back on
 *     close, if it is still in the document — and not if it is not, because a
 *     detached node is the same as nothing.
 */
export function Modal({
  label,
  narrow = false,
  wide = false,
  full = false,
  className,
  onClose,
  children,
}: {
  label: string;
  narrow?: boolean;
  wide?: boolean;
  /** Nearly the whole window, for a picture rather than a form: the whole graph. */
  full?: boolean;
  /** A caller's own variant, styled by its own sheet (the network's bottom sheet). */
  className?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  /* Captured while the component is being constructed rather than in the effect,
     so it is the element the reader actually left: by the time an effect runs,
     focus may already have moved. */
  const opener = useRef<Element | null>(
    typeof document === 'undefined' ? null : document.activeElement,
  );

  useEffect(() => {
    const node = panel.current;
    if (!node) return;
    (focusable(node)[0] ?? node).focus();

    return () => {
      const previous = opener.current;
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const node = panel.current;
      if (!node) return;
      const stops = focusable(node);
      if (stops.length === 0) {
        event.preventDefault();
        node.focus();
        return;
      }
      const first = stops[0] as HTMLElement;
      const last = stops[stops.length - 1] as HTMLElement;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !node.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !node.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /* Portalled to `<body>`, which is the only place a scrim can promise what
     `aria-modal` says. Mounted where it is written, the scrim is stacked inside
     whatever context an ancestor opened — and one of the callers now sits in a
     sticky column, which opens one whether it asks to or not (portal.tsx has the
     full account). Everything else here is unchanged by the move: the click-away
     is on the scrim's own element rather than on a bubbling path, Escape and the
     tab trap are bound to the window, and React keeps context and event
     propagation running down the tree the component is *written* in, not the one
     it renders into. */
  return (
    <Portal>
      <div
        className="modal"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <div
          className={`modal-panel${narrow ? ' narrow' : ''}${wide ? ' wide' : ''}${full ? ' full' : ''}${className ? ` ${className}` : ''}`}
          role="dialog"
          aria-label={label}
          aria-modal="true"
          ref={panel}
          tabIndex={-1}
        >
          {children}
        </div>
      </div>
    </Portal>
  );
}

/**
 * The panel's tab stops, in document order. A disabled control is not a stop and
 * neither is anything taken out of the order explicitly — which is what keeps
 * the panel's own `tabIndex={-1}` from being counted as the first one.
 */
function focusable(root: HTMLElement): HTMLElement[] {
  const selector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return [...root.querySelectorAll<HTMLElement>(selector)];
}
